// src/pages/CRM/Finance/FinanceDashboard.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  Button, Chip, Divider, CircularProgress, Alert, Stack, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar
} from '@mui/material';
import {
  AccountBalance as FinanceIcon,
  Receipt as InvoiceIcon,
  Payment as PaymentIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as BackIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { hasPermission } from '../../../../utils/permissionUtils';
import axiosInstance from '../../../../utils/axiosInstance';
import { InvoiceStatusLabels, InvoiceStatusColors, PaymentMethodLabels } from '../Core/crmEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const FinanceDashboard = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    document.title = 'Finance | KlonIT CRM';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadDashboard();
    }
  }, [canView, chatbotId]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await axiosInstance.get('/api/crm/finance/dashboard', { params });
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load finance dashboard');
      }
    } catch (err) {
      console.error('Error loading finance dashboard:', err);
      setError('Failed to load finance dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to view the Finance module.</Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadDashboard}>Retry</Button>
        }>{error}</Alert>
      </Box>
    );
  }

  const contactsWithBalance = data?.contacts_with_balance || [];
  const recentInvoices = data?.recent_invoices || [];
  const recentPayments = data?.recent_payments || [];

  const statCards = [
    {
      title: 'Total Outstanding',
      value: formatCurrency(data?.total_outstanding),
      color: '#ef4444',
      bgColor: '#fef2f2',
      icon: <WarningIcon />,
    },
    {
      title: 'Total Received',
      value: formatCurrency(data?.total_received),
      color: '#22c55e',
      bgColor: '#f0fdf4',
      icon: <TrendingUpIcon />,
    },
    {
      title: 'Total Invoiced',
      value: formatCurrency(data?.total_invoiced),
      color: '#3b82f6',
      bgColor: '#eff6ff',
      icon: <MoneyIcon />,
    },
    {
      title: 'Overdue Invoices',
      value: data?.overdue_count || 0,
      color: '#f97316',
      bgColor: '#fff7ed',
      icon: <TrendingDownIcon />,
    },
  ];

  return (
    <Box sx={{ p: 3, fontFamily }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm-dashboard`)}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700, fontFamily }}>
            Finance Dashboard
          </Typography>
        </Stack>
        {canModify && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<InvoiceIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/finance/invoices`)}
              sx={{ fontFamily, textTransform: 'none', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
            >
              Create Invoice
            </Button>
            <Button
              variant="outlined"
              startIcon={<PaymentIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/finance/payments`)}
              sx={{ fontFamily, textTransform: 'none', borderColor: '#3b82f6', color: '#3b82f6' }}
            >
              Record Payment
            </Button>
          </Stack>
        )}
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontFamily, mb: 0.5 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, fontFamily, color: card.color }}>
                      {card.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: card.bgColor, color: card.color, width: 44, height: 44 }}>
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Contacts with Outstanding Balance */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily }}>
                Contacts with Outstanding Balance
              </Typography>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Contact</TableCell>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Email</TableCell>
                    <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Outstanding</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contactsWithBalance.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3, fontFamily, color: '#9ca3af' }}>
                        No contacts with outstanding balance
                      </TableCell>
                    </TableRow>
                  ) : (
                    contactsWithBalance.map((contact) => (
                      <TableRow key={contact.id} hover sx={{ cursor: 'pointer' }}>
                        <TableCell sx={{ fontFamily }}>{`${contact.first_name || ''} ${contact.last_name || ''}`.trim()}</TableCell>
                        <TableCell sx={{ fontFamily, color: '#6b7280' }}>{contact.email}</TableCell>
                        <TableCell align="right" sx={{ fontFamily, fontWeight: 600, color: '#ef4444' }}>
                          {formatCurrency(contact.outstanding)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Recent Invoices */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily }}>
                Recent Invoices
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(`/chatbot/${chatbotId}/crm/finance/invoices`)}
                sx={{ fontFamily, textTransform: 'none', color: '#3b82f6' }}
              >
                View All
              </Button>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Invoice #</TableCell>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Contact</TableCell>
                    <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Balance</TableCell>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3, fontFamily, color: '#9ca3af' }}>
                        No invoices yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentInvoices.map((invoice) => (
                      <TableRow
                        key={invoice.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/chatbot/${chatbotId}/crm/finance/invoices/${invoice.id}`)}
                      >
                        <TableCell sx={{ fontFamily }}>{invoice.invoice_number}</TableCell>
                        <TableCell sx={{ fontFamily }}>{invoice.contact ? `${invoice.contact.first_name || ''} ${invoice.contact.last_name || ''}`.trim() : '-'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily }}>
                          {formatCurrency(invoice.total_amount)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>
                          {formatCurrency(invoice.balance_due)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={InvoiceStatusLabels[invoice.status] || invoice.status}
                            color={InvoiceStatusColors[invoice.status] || 'default'}
                            size="small"
                            sx={{ fontFamily }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily, color: '#6b7280' }}>
                          {formatDate(invoice.invoice_date)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Recent Payments */}
        <Grid item xs={12}>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily }}>
                Recent Payments
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate(`/chatbot/${chatbotId}/crm/finance/payments`)}
                sx={{ fontFamily, textTransform: 'none', color: '#3b82f6' }}
              >
                View All
              </Button>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Contact</TableCell>
                    <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Method</TableCell>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Reference #</TableCell>
                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentPayments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, fontFamily, color: '#9ca3af' }}>
                        No payments yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentPayments.map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell sx={{ fontFamily }}>{payment.contact ? `${payment.contact.first_name || ''} ${payment.contact.last_name || ''}`.trim() : '-'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily, fontWeight: 600, color: '#22c55e' }}>
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={PaymentMethodLabels[payment.payment_method] || payment.payment_method}
                            size="small"
                            variant="outlined"
                            sx={{ fontFamily }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily, color: '#6b7280' }}>
                          {payment.reference_number || '-'}
                        </TableCell>
                        <TableCell sx={{ fontFamily, color: '#6b7280' }}>
                          {formatDate(payment.payment_date)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinanceDashboard;
